import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Printer, Sparkles, Loader2, ClipboardCheck, X } from "lucide-react";
import { showError } from "../lib/errorHandler";
import { openPCQ } from "../lib/pcq";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyRow = () => ({ characteristic: "", method: "", frequency: "", criteria: "", responsible: "" });
const emptyAction = () => ({ problem: "", cause: "", action: "" });

const RowEditor = ({ form, setForm, section, label }) => {
  const updateRow = (idx, field, value) => setForm(prev => ({ ...prev, [section]: prev[section].map((r, i) => i === idx ? { ...r, [field]: value } : r) }));
  const addRow = () => setForm(prev => ({ ...prev, [section]: [...(prev[section] || []), emptyRow()] }));
  const removeRow = (idx) => setForm(prev => ({ ...prev, [section]: prev[section].filter((_, i) => i !== idx) }));
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-sm">{label}</h4>
        <Button size="sm" variant="outline" onClick={addRow} data-testid={`pcq-add-${section}`}>
          <Plus className="h-3 w-3 mr-1" />Adicionar linha
        </Button>
      </div>
      <div className="border rounded overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-100">
            <tr>
              <th className="p-1 text-left">Caracteristica</th>
              <th className="p-1 text-left">Metodo</th>
              <th className="p-1 text-left">Freq./Amostragem</th>
              <th className="p-1 text-left">Criterio</th>
              <th className="p-1 text-left">Responsavel</th>
              <th className="p-1 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {(form[section] || []).map((row, idx) => (
              <tr key={`${section}-${idx}`} className="border-t">
                <td className="p-1"><Input className="h-7 text-xs" value={row.characteristic || ""} onChange={e => updateRow(idx, "characteristic", e.target.value)} /></td>
                <td className="p-1"><Input className="h-7 text-xs" value={row.method || ""} onChange={e => updateRow(idx, "method", e.target.value)} /></td>
                <td className="p-1"><Input className="h-7 text-xs" value={row.frequency || ""} onChange={e => updateRow(idx, "frequency", e.target.value)} /></td>
                <td className="p-1"><Input className="h-7 text-xs" value={row.criteria || ""} onChange={e => updateRow(idx, "criteria", e.target.value)} /></td>
                <td className="p-1"><Input className="h-7 text-xs" value={row.responsible || ""} onChange={e => updateRow(idx, "responsible", e.target.value)} /></td>
                <td className="p-1"><Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => removeRow(idx)}><X className="h-3 w-3" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const StringListEditor = ({ form, setForm, section, label }) => {
  const updateItem = (idx, value) => setForm(prev => ({ ...prev, [section]: prev[section].map((r, i) => i === idx ? value : r) }));
  const addItem = () => setForm(prev => ({ ...prev, [section]: [...(prev[section] || []), ""] }));
  const removeItem = (idx) => setForm(prev => ({ ...prev, [section]: prev[section].filter((_, i) => i !== idx) }));
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-sm">{label}</h4>
        <Button size="sm" variant="outline" onClick={addItem} data-testid={`pcq-add-${section}`}>
          <Plus className="h-3 w-3 mr-1" />Adicionar
        </Button>
      </div>
      <div className="space-y-1">
        {(form[section] || []).map((item, idx) => (
          <div key={`${section}-${idx}`} className="flex gap-2">
            <Input className="h-8 text-xs" value={item} onChange={e => updateItem(idx, e.target.value)} />
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => removeItem(idx)}><X className="h-3 w-3" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function QualityControlPlans() {
  const [list, setList] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(null);

  const fetchAll = async () => {
    try {
      const [r1, r2] = await Promise.all([axios.get(`${API}/quality-control-plans`), axios.get(`${API}/parts`)]);
      setList(r1.data);
      setParts(r2.data);
    } catch (e) { showError(e, "Erro a carregar PCQs"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const openNew = async (partId) => {
    if (!partId) { toast.error("Selecione uma peça"); return; }
    try {
      const res = await axios.get(`${API}/quality-control-plans/auto-fill/${partId}`);
      setForm(res.data);
      setEditing(null);
      setDialogOpen(true);
    } catch (e) { showError(e, "Erro a auto-preencher PCQ"); }
  };

  const openEdit = async (id) => {
    try {
      const res = await axios.get(`${API}/quality-control-plans/${id}`);
      setForm(res.data);
      setEditing(id);
      setDialogOpen(true);
    } catch (e) { showError(e, "Erro a carregar PCQ"); }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Apagar ${code}?`)) return;
    try {
      await axios.delete(`${API}/quality-control-plans/${id}`);
      toast.success("PCQ apagado");
      fetchAll();
    } catch (e) { showError(e, "Erro a apagar"); }
  };

  const save = async () => {
    setSubmitting(true);
    try {
      const payload = {
        part_id: form.part_id, code: form.code, color: form.color, revision: form.revision,
        raw_material_reception: form.raw_material_reception || [],
        production_startup: form.production_startup || [],
        during_production: form.during_production || [],
        nonconformity_criteria: form.nonconformity_criteria || [],
        corrective_actions: form.corrective_actions || [],
        final_control: form.final_control || [],
        traceability_items: form.traceability_items || [],
        approved_by: form.approved_by, approved_at: form.approved_at, notes: form.notes,
      };
      if (editing) {
        await axios.put(`${API}/quality-control-plans/${editing}`, payload);
        toast.success("PCQ atualizado");
      } else {
        await axios.post(`${API}/quality-control-plans`, payload);
        toast.success("PCQ criado");
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) { showError(e, "Erro a guardar"); }
    finally { setSubmitting(false); }
  };

  // Helpers para editar linhas
  const updateAction = (idx, field, value) => setForm(prev => ({ ...prev, corrective_actions: prev.corrective_actions.map((r, i) => i === idx ? { ...r, [field]: value } : r) }));
  const addAction = () => setForm(prev => ({ ...prev, corrective_actions: [...(prev.corrective_actions || []), emptyAction()] }));
  const removeAction = (idx) => setForm(prev => ({ ...prev, corrective_actions: prev.corrective_actions.filter((_, i) => i !== idx) }));

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><ClipboardCheck className="h-7 w-7 text-orange-500" />Planos de Controlo da Qualidade (PCQ)</h1>
          <p className="text-sm text-muted-foreground mt-1">Documentos PCQ-XXX-### por peça com 7 secções estruturadas</p>
        </div>
        <div className="flex gap-2 items-center">
          <select
            className="h-10 px-3 rounded-md border bg-background text-sm"
            onChange={(e) => openNew(e.target.value)}
            defaultValue=""
            data-testid="pcq-new-from-part"
          >
            <option value="">+ Novo PCQ a partir da peça...</option>
            {parts.map(p => <option key={p.id} value={p.id}>{p.code} — {p.description}</option>)}
          </select>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Documentos PCQ ({list.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Peça</TableHead>
                  <TableHead>Material</TableHead>
                  <TableHead>Cavidades</TableHead>
                  <TableHead>Revisão</TableHead>
                  <TableHead>Aprovado por</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map(q => (
                  <TableRow key={q.id}>
                    <TableCell className="font-mono font-bold">{q.code}</TableCell>
                    <TableCell>{q.part_code} — {q.part_description}</TableCell>
                    <TableCell>{q.material}</TableCell>
                    <TableCell>{q.cavities || "-"}</TableCell>
                    <TableCell>{q.revision}</TableCell>
                    <TableCell>{q.approved_by || "-"}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => openPCQ(q)} title="Imprimir PCQ" data-testid={`pcq-print-${q.code}`}><Printer className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(q.id)} title="Editar" data-testid={`pcq-edit-${q.code}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(q.id, q.code)} className="text-destructive" title="Apagar" data-testid={`pcq-delete-${q.code}`}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
                {list.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nenhum PCQ criado. Selecione uma peça acima para começar.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-orange-500" />{editing ? "Editar PCQ" : "Novo PCQ (auto-preenchido)"}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3">
                <div><Label className="text-xs">Código</Label><Input value={form.code || ""} onChange={e => setForm({ ...form, code: e.target.value })} data-testid="pcq-code" /></div>
                <div><Label className="text-xs">Cor</Label><Input value={form.color || ""} onChange={e => setForm({ ...form, color: e.target.value })} placeholder="Ex: Laranja" data-testid="pcq-color" /></div>
                <div><Label className="text-xs">Material</Label><Input value={form.material || ""} disabled /></div>
                <div><Label className="text-xs">Revisão</Label><Input value={form.revision || "01"} onChange={e => setForm({ ...form, revision: e.target.value })} /></div>
                <div><Label className="text-xs">Máquina</Label><Input value={form.machine_code || ""} disabled /></div>
                <div><Label className="text-xs">Molde</Label><Input value={form.mold_code || ""} disabled /></div>
                <div><Label className="text-xs">Cavidades</Label><Input value={form.cavities || ""} disabled /></div>
                <div><Label className="text-xs">Aprovado por</Label><Input value={form.approved_by || ""} onChange={e => setForm({ ...form, approved_by: e.target.value })} data-testid="pcq-approved-by" /></div>
              </div>

              <RowEditor form={form} setForm={setForm} section="raw_material_reception" label="1. Receção da Matéria-Prima" />
              <RowEditor form={form} setForm={setForm} section="production_startup" label="2. Arranque da Produção" />
              <RowEditor form={form} setForm={setForm} section="during_production" label="3. Controlo Durante Produção" />
              <StringListEditor form={form} setForm={setForm} section="nonconformity_criteria" label="4. Critérios de Não Conformidade" />

              <div className="mt-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-sm">5. Acções Correctivas</h4>
                  <Button size="sm" variant="outline" onClick={addAction}><Plus className="h-3 w-3 mr-1" />Adicionar</Button>
                </div>
                <div className="border rounded overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100"><tr><th className="p-1 text-left">Problema</th><th className="p-1 text-left">Possível Causa</th><th className="p-1 text-left">Acção Correctiva</th><th className="w-8"></th></tr></thead>
                    <tbody>{(form.corrective_actions || []).map((a, idx) => (
                      <tr key={`act-${idx}`} className="border-t">
                        <td className="p-1"><Input className="h-7 text-xs" value={a.problem || ""} onChange={e => updateAction(idx, "problem", e.target.value)} /></td>
                        <td className="p-1"><Input className="h-7 text-xs" value={a.cause || ""} onChange={e => updateAction(idx, "cause", e.target.value)} /></td>
                        <td className="p-1"><Input className="h-7 text-xs" value={a.action || ""} onChange={e => updateAction(idx, "action", e.target.value)} /></td>
                        <td className="p-1"><Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => removeAction(idx)}><X className="h-3 w-3" /></Button></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>

              <RowEditor form={form} setForm={setForm} section="final_control" label="6. Controlo Final" />
              <StringListEditor form={form} setForm={setForm} section="traceability_items" label="7. Rastreabilidade (itens obrigatórios na caixa)" />

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                <Button onClick={save} disabled={submitting} data-testid="pcq-save">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editing ? "Guardar Alterações" : "Criar PCQ"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
